import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../config/api";

function getApiBaseUrl() {
  return `${API_BASE_URL}/api`;
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatHours(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0,00";
  return num.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

async function readJson(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

export default function PublicMandagstaatSignaturePage() {
  const { token = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mandagstaat, setMandagstaat] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [finalState, setFinalState] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const response = await fetch(`${getApiBaseUrl()}/public/mandagstaten/sign/${encodeURIComponent(token)}`);
        const data = await readJson(response);
        if (!response.ok) {
          throw new Error(data?.message || "Ondertekenverzoek kon niet worden geladen");
        }
        if (cancelled) return;
        setMandagstaat(data?.mandagstaat || null);
        setPdfUrl(
          data?.pdfUrl
            ? `${getApiBaseUrl().replace(/\/api$/, "")}${String(data.pdfUrl)}`
            : ""
        );
        setTypedName(String(data?.mandagstaat?.signerName || ""));
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Ondertekenverzoek kon niet worden geladen");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleApprove() {
    if (!confirmed) {
      setError("Bevestig eerst dat je deze mandagstaat hebt gecontroleerd.");
      return;
    }
    if (!String(typedName || "").trim()) {
      setError("Vul je naam in om te ondertekenen.");
      return;
    }

    try {
      setSubmitting("approve");
      setError("");
      setSuccess("");
      const response = await fetch(
        `${getApiBaseUrl()}/public/mandagstaten/sign/${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            decision: "approve",
            typedName: String(typedName || "").trim(),
            confirmed: true,
          }),
        }
      );
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Ondertekenen mislukt");
      }
      setMandagstaat(data?.mandagstaat || mandagstaat);
      setFinalState("signed");
      setSuccess(data?.message || "Mandagstaat ondertekend");
    } catch (err) {
      setError(err?.message || "Ondertekenen mislukt");
    } finally {
      setSubmitting("");
    }
  }

  async function handleReject() {
    if (!String(rejectReason || "").trim()) {
      setError("Vul eerst een reden van afwijzing in.");
      return;
    }

    try {
      setSubmitting("reject");
      setError("");
      setSuccess("");
      const response = await fetch(
        `${getApiBaseUrl()}/public/mandagstaten/sign/${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            decision: "reject",
            note: String(rejectReason || "").trim(),
          }),
        }
      );
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Afwijzen mislukt");
      }
      setMandagstaat(data?.mandagstaat || mandagstaat);
      setFinalState("rejected");
      setSuccess(data?.message || "Ondertekenverzoek afgewezen");
    } catch (err) {
      setError(err?.message || "Afwijzen mislukt");
    } finally {
      setSubmitting("");
    }
  }

  const statusLabel =
    finalState === "signed"
      ? "Ondertekend"
      : finalState === "rejected"
        ? "Afgewezen"
        : mandagstaat?.status === "pending_signature"
          ? "Wacht op ondertekening"
          : mandagstaat?.status || "-";

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10 text-slate-900">
      <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            Logboek24
          </p>
          <h1 className="mt-2 text-3xl font-semibold">Mandagstaat ondertekenen</h1>
          <p className="mt-2 text-sm text-slate-600">
            Controleer de mandagstaat en geef digitaal akkoord of wijs deze af.
          </p>
        </div>

        <div className="px-6 py-6">
          {loading ? <p className="text-sm text-slate-600">Ondertekenverzoek laden...</p> : null}

          {error ? (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {success ? (
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {success}
            </div>
          ) : null}

          {!loading && mandagstaat ? (
            <div className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <Info label="Referentie" value={mandagstaat.reference || "-"} />
                <Info label="Week / Jaar" value={`${mandagstaat.weekNumber} / ${mandagstaat.year}`} />
                <Info label="Scope" value={mandagstaat.scopeLabelSnapshot || "-"} />
                <Info label="Totaal uren" value={`${formatHours(mandagstaat.totalHoursWeek)} uur`} />
                <Info label="Ondertekenaar" value={mandagstaat.signerName || "-"} />
                <Info label="E-mail" value={mandagstaat.signerEmail || "-"} />
                <Info label="Vervaldatum" value={formatDate(mandagstaat.expiresAt)} />
                <Info label="Status" value={statusLabel} />
              </div>

              {pdfUrl ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <button
                    type="button"
                    onClick={() => window.open(pdfUrl, "_blank", "noopener,noreferrer")}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
                  >
                    PDF bekijken / downloaden
                  </button>
                </div>
              ) : null}

              {!finalState ? (
                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="rounded-xl border border-slate-200 px-4 py-4">
                    <h2 className="text-lg font-semibold">Akkoord geven</h2>
                    <div className="mt-4 space-y-4">
                      <label className="flex items-start gap-3 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={confirmed}
                          onChange={(event) => setConfirmed(event.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-slate-300"
                        />
                        <span>Ik heb deze mandagstaat gecontroleerd en ga akkoord.</span>
                      </label>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Naam ter bevestiging
                        </label>
                        <input
                          type="text"
                          value={typedName}
                          onChange={(event) => setTypedName(event.target.value)}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          placeholder="Vul je naam in"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleApprove}
                        disabled={submitting === "approve"}
                        className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                      >
                        {submitting === "approve" ? "Ondertekenen..." : "Akkoord / Ondertekenen"}
                      </button>
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 px-4 py-4">
                    <h2 className="text-lg font-semibold">Afwijzen</h2>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Reden van afwijzing
                        </label>
                        <textarea
                          value={rejectReason}
                          onChange={(event) => setRejectReason(event.target.value)}
                          rows={5}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          placeholder="Omschrijf waarom je deze mandagstaat afwijst"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleReject}
                        disabled={submitting === "reject"}
                        className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 disabled:opacity-60"
                      >
                        {submitting === "reject" ? "Afwijzen..." : "Afwijzen"}
                      </button>
                    </div>
                  </section>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                  {finalState === "signed"
                    ? "Deze mandagstaat is digitaal ondertekend."
                    : "Dit ondertekenverzoek is afgewezen."}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-900">{value}</p>
    </div>
  );
}
